import { useEffect, useRef, useState } from "react";

/**
 * WebGL particle field behind the home hero.
 *
 * three.js is ~600KB and this effect only runs on desktop pointers with motion enabled,
 * so the library is imported dynamically inside the effect. Every other visitor — and
 * every other route — never downloads it, and the CSS fallback renders instead.
 */
const isDesktop = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(min-width: 900px)").matches &&
  !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const makeSprite = (THREE, octagon) => {
  const canvas = document.createElement("canvas");
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext("2d");
  ctx.translate(32, 32);
  if (octagon) {
    ctx.beginPath();
    for (let i = 0; i < 8; i += 1) {
      const a = (i / 8) * Math.PI * 2 - Math.PI / 8;
      const x = Math.cos(a) * 24;
      const y = Math.sin(a) * 24;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.shadowColor = "rgba(255,255,255,0.9)";
    ctx.shadowBlur = 14;
    ctx.fillStyle = "rgba(255,255,255,1)";
    ctx.fill();
  } else {
    const g = ctx.createRadialGradient(0, 0, 0, 0, 0, 30);
    g.addColorStop(0, "rgba(255,255,255,1)");
    g.addColorStop(0.45, "rgba(255,255,255,0.8)");
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.fillRect(-32, -32, 64, 64);
  }
  return new THREE.CanvasTexture(canvas);
};

const buildCloud = (THREE, count, color, size, opacity, spread, octagon) => {
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i += 1) {
    const r = spread * Math.cbrt(Math.random());
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    positions[i * 3] = r * Math.sin(phi) * Math.cos(theta) * 1.6;
    positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta) * 0.75;
    positions[i * 3 + 2] = r * Math.cos(phi);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const material = new THREE.PointsMaterial({
    color, size, transparent: true, opacity, depthWrite: false, sizeAttenuation: true,
    map: makeSprite(THREE, octagon), blending: THREE.AdditiveBlending,
  });
  return new THREE.Points(geometry, material);
};

const HeroField = () => {
  const mountRef = useRef(null);
  const [enabled] = useState(isDesktop);

  useEffect(() => {
    if (!enabled || !mountRef.current) return undefined;
    const mount = mountRef.current;
    let cleanup = () => {};
    let cancelled = false;

    import("three").then((THREE) => {
      // The component may have unmounted while the chunk was in flight.
      if (cancelled || !mount.isConnected) return;

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(55, mount.clientWidth / mount.clientHeight, 0.1, 100);
      camera.position.z = 9;
      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.8));
      renderer.setSize(mount.clientWidth, mount.clientHeight);
      mount.appendChild(renderer.domElement);

      const white = buildCloud(THREE, 2600, 0xffffff, 0.05, 0.5, 5.4, false);
      const volt = buildCloud(THREE, 650, 0x00b898, 0.09, 0.75, 4.4, true);
      const group = new THREE.Group();
      group.add(white);
      group.add(volt);
      group.position.x = 2.2;
      scene.add(group);

      let mouseX = 0;
      let mouseY = 0;
      const onMouse = (e) => {
        mouseX = e.clientX / window.innerWidth - 0.5;
        mouseY = e.clientY / window.innerHeight - 0.5;
      };
      const onResize = () => {
        camera.aspect = mount.clientWidth / mount.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(mount.clientWidth, mount.clientHeight);
      };
      window.addEventListener("mousemove", onMouse);
      window.addEventListener("resize", onResize);

      const clock = new THREE.Clock();
      let rafId;
      const animate = () => {
        const t = clock.getElapsedTime();
        group.rotation.y = t * 0.07 + mouseX * 0.4;
        group.rotation.x = Math.sin(t * 0.12) * 0.08 + mouseY * 0.25;
        volt.rotation.y = -t * 0.05;
        renderer.render(scene, camera);
        rafId = requestAnimationFrame(animate);
      };
      animate();

      cleanup = () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener("mousemove", onMouse);
        window.removeEventListener("resize", onResize);
        [white, volt].forEach((p) => {
          p.geometry.dispose();
          p.material.dispose();
        });
        renderer.dispose();
        if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
      };
    });

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [enabled]);

  if (!enabled) {
    return <div className="absolute inset-0 hero-fallback" aria-hidden="true" data-testid="hero-fallback-bg" />;
  }
  // The CSS gradient sits underneath so the hero is never empty while the chunk loads.
  return (
    <div className="absolute inset-0 hero-fallback" aria-hidden="true">
      <div ref={mountRef} className="absolute inset-0" data-testid="hero-webgl-canvas" />
    </div>
  );
};

export default HeroField;
